import {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useInView } from 'react-intersection-observer'
import styled, { css } from 'styled-components'

import nanoid from '@utils/random'
import config from '@config'
import useUser, { User } from '@hooks/useUser'
import useAuthentication from '@hooks/useAuthentication'
import useSSOTrackingSource from '@hooks/useSSOTrackingSource'
import useDisableThirdPartyScripts from '@hooks/useDisableThirdPartyScripts'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'

export interface EmbeddedContentType {
  url?: string
  ['max-width']?: number
  ['max-height']?: number
  ['max-height-mobile']?: number
  scrollingEnabled?: boolean
  htmlCode?: string
  withSSO?: boolean
  parentProps?: {
    inline?: boolean
  }
}

export interface EmbeddedContentProps extends EmbeddedContentType {
  user?: User
  injectHtml?: string
  login?: any
  inScribble?: boolean
  style?: any
  onScrollToRequest?: (offset: number) => void
}

export interface EmbeddedContentWebProps extends EmbeddedContentProps {
  heightFieldName?: string
  idFieldName?: string
  id?: string
  maxWidth?: number
  maxHeight?: number
  maxHeightMobile?: number
}

const {
  iframe: { heightAdjustmentScript, allowedOriginsToReceivePostMessage },
} = config

type StyledEmbeddedContentContainer = Pick<
  EmbeddedContentWebProps,
  'maxHeight' | 'maxHeightMobile'
>

const StyledEmbeddedContentContainer = styled.div<StyledEmbeddedContentContainer>`
  ${({ maxHeight, maxHeightMobile }) => css`
    width: 100%;
    position: relative;

    ${desktopCSS(css`
      height: ${maxHeight ?? 200}px;
    `)}

    ${tabletCSS(css`
      height: ${maxHeight ?? 200}px;
    `)}

    ${mobileCSS(css`
      height: ${maxHeightMobile ?? 200}px;
    `)}
  `}
`

const StyledIFrame = styled.iframe`
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border: none;
  height: 100%;
  width: 1px;
  min-width: 100%;
  margin: 0;
`

const EmbeddedContent: FunctionComponent<EmbeddedContentWebProps> = ({
  url,
  scrollingEnabled,
  htmlCode,
  'max-height': maxHeight,
  'max-height-mobile': maxHeightMobile,
  withSSO,
}) => {
  const disableThirdPartyScripts = useDisableThirdPartyScripts()
  const user = useUser()
  const iframeIdRef = useRef<string>('')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const setIframeRef: (node?: HTMLIFrameElement | null) => void = (node) => {
    if (node) {
      iframeRef.current = node
      setupHtmlCodeIframe(node)
    }
  }

  const [loadNow, setLoadNow] = useState(false)

  const doLoadNow = useCallback<(isInView: boolean) => void>((isInView) => {
    if (isInView) {
      if (!iframeIdRef.current) {
        iframeIdRef.current = nanoid()
      }
      setLoadNow(true)
    }
  }, [])

  const { ref: setInViewEmbeddedContentContainerRef } = useInView({
    rootMargin: '100% 0px 100% 0px',
    onChange: doLoadNow,
    triggerOnce: true,
  })

  const [ssoTrackingSource, setSSOTrackingSource] = useState<string>('')
  const embeddedContentContainerRef = useRef<HTMLDivElement>()
  const setEmbeddedContentContainerRef = useCallback<
    (node?: Element | null | undefined) => void
  >(
    (node) => {
      if (node) {
        embeddedContentContainerRef.current = node as HTMLDivElement
        setInViewEmbeddedContentContainerRef(node)
      }
    },
    [setInViewEmbeddedContentContainerRef]
  )

  const ssoSource = useSSOTrackingSource({
    prefix: ssoTrackingSource,
  })

  const { login } = useAuthentication({ prefix: '' })

  const sendUserData = useCallback(() => {
    if (withSSO && url) {
      // Only send the user data down to allowed origins
      // defined in the config by us
      const embeddedOrigin = new URL(url).origin
      if (allowedOriginsToReceivePostMessage.includes(embeddedOrigin)) {
        iframeRef?.current?.contentWindow?.postMessage({ user }, embeddedOrigin)
      }
    }
  }, [url, user, withSSO])

  const onHandlePostMessage = useCallback(
    (event: MessageEvent) => {
      const {
        origin,
        data: {
          height,
          iframeId: hostedIFrameId,
          loginAction,
          ssoTrackingSource,
          iframeReady,
        },
      } = event

      if (iframeReady) {
        sendUserData()
        return
      }

      if (url && !url.startsWith(origin)) {
        return
      }

      // iFrames are either tracked by their unique id on the iframe tag or by their url as a fallback
      if ([iframeIdRef.current, url].includes(hostedIFrameId)) {
        const currentIFrameHeight =
          embeddedContentContainerRef.current?.clientHeight
        if (
          !!embeddedContentContainerRef.current &&
          !isNaN(height) &&
          height !== currentIFrameHeight
        ) {
          embeddedContentContainerRef.current.style.height = `${height}px`
        }
      }

      // Certain embeds can trigger a login action through their post message
      // Here we check if the action was true and if it was sent by the actual component
      if (loginAction) {
        if (iframeRef.current?.id === iframeIdRef.current) {
          setSSOTrackingSource(ssoTrackingSource || 'IFR_')
        }
      }
    },
    [url, sendUserData]
  )

  const setupHtmlCodeIframe = useCallback<(node: HTMLIFrameElement) => void>(
    (node) => {
      // If the iframe is just an html code we have to add the content
      // and the height adjustment script to the body of the iframe
      const document = node.contentDocument
      if (document && htmlCode) {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = heightAdjustmentScript

        // Due to some special iframes with embedded scripts we cannot
        // just call: document.body.innerHTML = htmlCode, so we have to use
        // the good ol' document.write
        document.open()
        document.write(htmlCode)
        document.body.append(script)
        document.close()
      }
    },
    [htmlCode]
  )

  useEffect(() => {
    sendUserData()
  }, [sendUserData])

  useEffect(() => {
    // When a sso tracking source was set on the specific embedded content
    // trigger the login with this specific source
    if (ssoTrackingSource) {
      login({ source: ssoSource })
    }
  }, [ssoTrackingSource, login, ssoSource])

  useEffect(() => {
    window.addEventListener('message', onHandlePostMessage)

    return () => {
      window.removeEventListener('message', onHandlePostMessage)
    }
  }, [onHandlePostMessage])

  if (disableThirdPartyScripts) {
    return null
  }

  return (
    <StyledEmbeddedContentContainer
      maxHeight={maxHeight}
      maxHeightMobile={maxHeightMobile}
      ref={setEmbeddedContentContainerRef}>
      {loadNow && (
        <StyledIFrame
          id={iframeIdRef.current}
          ref={setIframeRef}
          src={url}
          title="Embedded Content"
          loading="eager"
          scrolling={scrollingEnabled ? 'yes' : 'no'}
          allowFullScreen
        />
      )}
    </StyledEmbeddedContentContainer>
  )
}

const MemoizedEmbeddedContent = memo(EmbeddedContent)

export default MemoizedEmbeddedContent
